/**
 * The route table.
 *
 * Data loading lives in `loader`, not in components. That is the direct
 * replacement for the async server components this app used to be built from:
 * the router awaits the loader before rendering, so a page still receives its
 * data as a prop-like value rather than rendering a spinner and filling in
 * afterwards. Layout routes load once and their children re-render on
 * navigation, which is what keeps the case header and tabs on screen while
 * moving between tabs.
 *
 * A loader that throws a `Response` is handled by the nearest `errorElement`,
 * which is how `notFound()` and the auth redirect are expressed here.
 */
import { createBrowserRouter, redirect } from "react-router";
import { RootErrorBoundary } from "./routes/root-error";
import { NotFound } from "./routes/not-found";
import { LoginPage, loginLoader } from "./routes/login";
import { WorkspaceLayout, workspaceLoader } from "./routes/workspace";
import { InboxPage, inboxLoader } from "./routes/inbox";
import { ReportsPage, reportsLoader } from "./routes/reports";
import { IntegrationsPage, integrationsLoader } from "./routes/integrations";
import { AdminPage, adminLoader } from "./routes/admin";
import { BillingPage, billingLoader } from "./routes/billing";
import { BorrowerPage, borrowerLoader } from "./routes/borrower";
import { CaseLayout, caseLoader } from "./routes/case";
import { CaseOverview } from "./components/case/overview";
import { EvidenceView } from "./components/evidence/evidence-view";
import { AnalysisView } from "./components/analysis/analysis-view";
import { DecisionView } from "./components/decision/decision-view";
import { ActivityTimeline } from "./components/activity/activity-timeline";

export const router = createBrowserRouter([
  {
    errorElement: <RootErrorBoundary />,
    children: [
      { path: "/login", element: <LoginPage />, loader: loginLoader },
      {
        // Named so the case layout can read the resolved session from it
        // instead of asking the backend who we are once per case.
        id: "workspace",
        element: <WorkspaceLayout />,
        loader: workspaceLoader,
        children: [
          // There is no product home. Work starts in the inbox.
          { index: true, loader: () => redirect("/inbox") },
          { path: "/inbox", element: <InboxPage />, loader: inboxLoader },
          { path: "/reports", element: <ReportsPage />, loader: reportsLoader },
          {
            path: "/integrations",
            element: <IntegrationsPage />,
            loader: integrationsLoader,
          },
          { path: "/admin", element: <AdminPage />, loader: adminLoader },
          { path: "/billing", element: <BillingPage />, loader: billingLoader },
          {
            path: "/borrowers/:borrowerId",
            element: <BorrowerPage />,
            loader: borrowerLoader,
          },
          {
            path: "/cases/:caseId",
            element: <CaseLayout />,
            loader: caseLoader,
            children: [
              { index: true, element: <CaseOverview /> },
              { path: "evidence", element: <EvidenceView /> },
              { path: "analysis", element: <AnalysisView /> },
              { path: "decision", element: <DecisionView /> },
              { path: "activity", element: <ActivityTimeline /> },
            ],
          },
        ],
      },
      { path: "*", element: <NotFound /> },
    ],
  },
]);
